import * as React from 'react'
import { ReportType } from '@/app/constants/reportType'
import { useGeneratePrintout } from '../hooks/useGeneratePrintout'
import { useReportCapabilities } from '../hooks/useReportCapabilities'
import { PrintActionButton } from '../components/PrintActionButton'
import { PrintModal, PrintOptions } from '../components/PrintModal'

interface Props {
  tooltip?: string
  companyId: string
  dateFrom?: string
  dateTo?: string
  driverIds: string[]
  availableReports: ReportType[]

  // optional controlled state
  isOpen?: boolean
  setOpen?: (open: boolean) => void
}

export const PrintActionContainer: React.FC<Props> = ({
  tooltip,
  companyId,
  dateFrom,
  dateTo,
  driverIds,
  availableReports,
  isOpen: controlledOpen,
  setOpen: controlledSetOpen,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const isControlled =
    controlledOpen !== undefined && controlledSetOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? controlledSetOpen : setInternalOpen
  const [sending, setSending] = React.useState<boolean>(false)

  const { handleGeneratePrintout, isGenerating } = useGeneratePrintout()
  const capabilities = useReportCapabilities(isOpen ? companyId : '')

  const onGenerate = async (
    selectedReports: ReportType[],
    options: PrintOptions,
  ) => {
    if (sending) return

    setSending(true)
    try {
      await handleGeneratePrintout({
        companyId,
        driverIds,
        dateFrom: options.dateFrom || dateFrom,
        dateTo: options.dateTo || dateTo,
        selectedReports,
        generateToSeparateFiles: options.generateToSeparateFiles,
        rememberSelected: options.rememberSelected,
        issuerSignature: options.issuerSignature,
        driverSignature: options.driverSignature,
        outputType: options.outputType,
      })
      setOpen(false)
    } catch (error) {
      console.error('Error generating printout:', error)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {!isControlled && (
        <PrintActionButton
          tooltip={tooltip}
          onClick={() => setOpen(true)}
          disabled={isGenerating}
        />
      )}

      <PrintModal
        pending={sending || isGenerating}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        availableReports={availableReports}
        capabilities={capabilities}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onGenerate={onGenerate}
      />
    </>
  )
}
